<template>
  <div
    v-if="picture && pictureParams"
    class="gallery-picture-detail w-full h-full flex items-center justify-between flex-col"
  >
    <div class="picture-wrapper relative w-full h-full">
      <div
        class="picture-wrapper-img"
        :style="{
          backgroundImage: 'url(' + imgPath + ')',
        }"
      />

      <gallery-picture-pieces-overlay
        v-if="!isLocked && !isLockedByGems"
        :users-pieces="piecesUserCanInsert"
      />
    </div>

    <div v-if="isLocked" class="picture-locked-info-wrapper w-full h-full flexing flex-col">
      <app-icon class="lock mb-5" icon-name="lock-xxl" />

      <div class="picture-locked-outer-line text-texts-standard-default text-32 flexing">
        <span v-if="isPremium">{{ $t('gallery.pictureTaskAndGems') }}</span>
        <span v-else>{{ $t('gallery.pictureUnlockInfo') }}</span>
      </div>

      <div class="picture-locked-middle-line text-texts-standard-default text-32 flexing">
        <div v-html="lockedText"></div>
      </div>

      <div
        v-if="isLockedByPersonalBest"
        class="picture-locked-outer-line text-texts-standard-default text-32 uppercase flexing"
      >
        <div>
          {{ $t('gallery.personalBest') }}:
          <span class="text-texts-standard-important">{{
            formatResult(picture.personal_best, pictureParams.discipline)
          }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="isLockedByGems || (isLocked && isPremium)"
      class="picture-premium-info-wrapper w-full h-full flex items-center flex-col pb-6 gap-6"
      :class="isLockedByGems ? 'justify-end' : 'justify-between'"
    >
      <div
        class="picture-premium-first-line text-texts-standard-premium text-36 uppercase flexing z-100 mt-10 font-bold"
      >
        {{ $t('gallery.premiumPicture') }}
      </div>

      <div
        v-if="isLockedByGems"
        class="picture-premium-second-line w-full text-texts-standard-default text-32 flexing"
      >
        {{ $t('gallery.pictureUnlockForGems') }}
      </div>

      <app-multi-button1
        class="gem-button mt-6"
        btn-type="credit"
        btn-icon="gems"
        btn-size="md"
        :disabled="!isLockedByGems"
        :btn-id="unlockBtnId"
        :btn-text="$t('gallery.unlock')"
        :btn-count="pictureParams.price ?? 0"
        :popup-data="{ ...btnPopupData, btnValue: pictureParams.price ?? 0 }"
        @action="unlockPicture(picture.id)"
      />
    </div>

    <div
      v-if="isCompleted"
      class="picture-completed text-texts-standard-default text-32 uppercase flexing"
    >
      {{ $t('gallery.pictureDone') }}
      <app-icon class="done" icon-name="done-sm" />
    </div>

    <div
      v-if="isLocked || isLockedByGems"
      class="picture-locked text-texts-standard-default text-32 uppercase flexing"
      :class="{ 'picture-locked-premium': isPremium }"
    >
      {{ $t('gallery.lockedPicture') }}
    </div>

    <div
      v-if="canPlacePieces"
      class="picture-info-click-pieces-wrapper w-full text-texts-standard-default text-32 flex items-center justify-between"
    >
      <div class="flexing">
        <app-icon class="icon-info mr-4" icon-name="info-rounded" />
        {{ $t('gallery.clickPieceToSlot') }}
      </div>

      <app-button
        btn-type="primary"
        :disabled="piecesUserCanInsert.length === 0"
        :btn-text="$t('gallery.slotAllPieces')"
        @click="showSlotAllPopup = true"
      />
    </div>

    <info-popup
      v-if="showSlotAllPopup"
      :popup-title="$t(`gallery.${pictureParams.name}`)"
      @close="showSlotAllPopup = false"
    >
      <gallery-slot-all-popup :pieces="piecesUserCanInsert" @close="showSlotAllPopup = false" />
    </info-popup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { pathImages } from '@/globalVariables'
import { mapState, mapActions } from 'pinia'
import { formatResult } from '@/helpers'
import AppIcon from '../GlobalComponents/AppIcon.vue'
import GalleryPicturePiecesOverlay from './GalleryPicturePiecesOverlay.vue'
import {
  GalleryPictureState,
  GalleryPictureUnlockCondition,
  GalleryPieceRarityShorthand,
} from '@/interfaces/gallery/enums'
import type { GalleryPicture, PieceCountByRarity } from '@/interfaces/gallery/Pictures'
import AppButton from '../GlobalComponents/AppButton.vue'
import InfoPopup from '../Popup/InfoPopup.vue'
import GallerySlotAllPopup from './GallerySlotAllPopup.vue'
import { useGalleryPictureStore } from '@/store/pinia/gallery/pictureStore'
import { type GalleryHeaderParams, useResponseTaskStore } from '@/store/pinia/responseTaskStore'

interface ComponentData {
  pathImages: typeof pathImages
  showSlotAllPopup: boolean
}

const UNLOCK_BTN_ID = 'btn-unlock-gallery-picture'

export default defineComponent({
  name: 'GalleryPictureDetail',

  components: {
    AppIcon,
    GalleryPicturePiecesOverlay,
    AppButton,
    InfoPopup,
    GallerySlotAllPopup,
  },

  data(): ComponentData {
    return {
      pathImages,
      showSlotAllPopup: false,
    }
  },

  computed: {
    ...mapState(useGalleryPictureStore, ['picture']),
    ...mapState(useResponseTaskStore, ['getGalleryParams']),

    imgPath(): string {
      const inactiveStr = this.isLocked || this.isLockedByGems ? '-blur' : ''
      return `${pathImages}gallery/picture-detail/gallery-picture-${this.picture?.parameters?.id}${inactiveStr}.avif`
    },

    unlockBtnId(): string {
      return UNLOCK_BTN_ID
    },

    pictureParams(): GalleryPicture['parameters'] | null {
      if (!this.picture || !this.picture.parameters) return null
      return this.picture.parameters
    },

    isPremium(): boolean {
      return !this.pictureParams.is_free
    },

    isLocked(): boolean {
      return this.picture.state === GalleryPictureState.Locked
    },

    isLockedByGems(): boolean {
      return this.isPremium && this.picture.state === GalleryPictureState.Redeemable
    },

    isCompleted(): boolean {
      return (
        this.picture?.state === GalleryPictureState.CompletedClaimed ||
        this.picture?.state === GalleryPictureState.CompletedUnclaimed
      )
    },

    canPlacePieces(): boolean {
      return !this.isLocked && !this.isLockedByGems && !this.isCompleted
    },

    unlockCondition(): GalleryPictureUnlockCondition | '' {
      return this.pictureParams?.unlock_condition_type || ''
    },

    isLockedByPersonalBest(): boolean {
      return this.unlockCondition === GalleryPictureUnlockCondition.PersonalBest
    },

    lockedText(): string {
      return this.getLockedText(
        this.unlockCondition,
        this.pictureParams.unlock_condition_value,
        this.pictureParams.discipline || 0,
      )
    },

    btnPopupData(): any {
      return this.$filters.$confirmPopupData({
        title: this.pictureParams.name,
        text: this.$t('gallery.unlockConfirmMessage'),
        btnId: UNLOCK_BTN_ID,
        btnText: this.$t('gallery.unlock'),
        btnValue: 0,
      })
    },

    piecesUserCanInsert(): PieceCountByRarity[] {
      const rarities = Object.values(GalleryPieceRarityShorthand)
      const result: PieceCountByRarity[] = []

      for (const rarity of rarities) {
        let required = this.pictureParams[`required_pieces_${rarity}`]
        if (this.picture.to_assemble_by_rarity)
          required = this.picture.to_assemble_by_rarity[`gallery_pieces_${rarity}`]

        const owned =
          this.getGalleryParams?.find(
            (galleryParam: GalleryHeaderParams): boolean =>
              galleryParam.parameter_name === `gallery_pieces_${rarity}`,
          )?.value || 0

        if (required === 0 || owned === 0) continue
        else
          result.push({
            rarity,
            count: Math.min(required, owned), // I can insert only as many as I have
          })
      }

      return result
    },
  },

  methods: {
    ...mapActions(useGalleryPictureStore, ['unlockPicture', 'getLockedText']),
    formatResult,
  },
})
</script>

<style lang="scss" scoped>
$imgWidth: 82rem;
$imgHeight: 40.5rem;

.gallery-picture-detail {
  position: relative;

  .picture-wrapper {
    width: 100%;
    height: 100%;

    &-img {
      width: $imgWidth;
      height: $imgHeight;
      background-repeat: no-repeat;
      background-size: cover;
      position: absolute;
      inset: 0;
      left: 50%;
      transform: translateX(-50%);

      @if $isWsm {
        outline: solid 0.25rem #5ba3dc;
      }
      @if $isSsm {
        outline: solid 0.25rem #4c648f;
      }
    }
  }

  .picture-locked-info-wrapper {
    width: $imgWidth;
    height: $imgHeight;
    position: absolute;
    inset: 0;
    left: 50%;
    transform: translateX(-50%);
    .lock {
      width: 9.3125rem;
      height: 10.25rem;
    }
    .picture-locked-outer-line {
      width: 100%;
      height: 3.125rem;

      @if $isWsm {
        background-image: linear-gradient(
          90deg,
          rgba(55, 127, 184, 0) 0%,
          rgba(55, 127, 184, 0.8) 15%,
          rgba(55, 127, 184, 0.8) 85%,
          rgba(55, 127, 184, 0) 100%
        );
      }
      @if $isSsm {
        background-image: linear-gradient(
          90deg,
          rgba(44, 54, 92, 0) 0%,
          rgba(44, 54, 92, 0.8) 15%,
          rgba(44, 54, 92, 0.8) 85%,
          rgba(44, 54, 92, 0) 100%
        );
      }
    }
    .picture-locked-middle-line {
      width: 100%;
      height: 7.5rem;
      @if $isWsm {
        background-image: linear-gradient(
          90deg,
          rgba(108, 158, 196, 0) 0%,
          rgba(108, 158, 196, 0.8) 15%,
          rgba(108, 158, 196, 0.8) 85%,
          rgba(108, 158, 196, 0) 100%
        );
      }
      @if $isSsm {
        background-image: linear-gradient(
          90deg,
          rgba(86, 98, 127, 0) 0%,
          rgba(86, 98, 127, 0.8) 15%,
          rgba(86, 98, 127, 0.8) 85%,
          rgba(86, 98, 127, 0) 100%
        );
      }
    }
  }

  .picture-locked {
    width: 100%;
    height: 4.375rem;
    @if $isWsm {
      background-image: linear-gradient(
        90deg,
        rgba(108, 158, 196, 0) 0%,
        rgba(108, 158, 196, 0.8) 15%,
        rgba(108, 158, 196, 0.8) 85%,
        rgba(108, 158, 196, 0) 100%
      );
    }
    @if $isSsm {
      background-image: linear-gradient(
        90deg,
        rgba(86, 98, 127, 0) 0%,
        rgba(86, 98, 127, 0.8) 15%,
        rgba(86, 98, 127, 0.8) 85%,
        rgba(86, 98, 127, 0) 100%
      );
    }

    &.picture-locked-premium {
      background-image: linear-gradient(
        90deg,
        rgba(110, 9, 178, 0) 0%,
        rgba(110, 9, 178, 0.8) 15%,
        rgba(110, 9, 178, 0.8) 85%,
        rgba(110, 9, 178, 0) 100%
      );
    }
  }
  .picture-completed {
    width: 100%;
    height: 4.375rem;
    background-image: linear-gradient(
      90deg,
      rgba(14, 231, 204, 0) 0%,
      rgba(14, 231, 204, 0.8) 15%,
      rgba(14, 231, 204, 0.8) 85%,
      rgba(14, 231, 204, 0) 100%
    );

    .done {
      width: 3.125rem;
      height: 3.125rem;
      margin: 0 0.5rem;
    }
  }
  .picture-premium-info-wrapper {
    width: $imgWidth;
    height: $imgHeight;
    position: absolute;
    inset: 0;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: inset 0 0 11.25rem 0 rgba(134, 28, 212, 0.7);

    .picture-premium-first-line {
      text-shadow: 0 0 0.5rem #000;
      filter: drop-shadow(0 0 0.5rem #000);
    }

    .picture-premium-second-line {
      @if $isWsm {
        background-image: linear-gradient(
          90deg,
          rgba(55, 127, 184, 0) 0%,
          rgba(55, 127, 184, 0.8) 15%,
          rgba(55, 127, 184, 0.8) 85%,
          rgba(55, 127, 184, 0) 100%
        );
      }
      @if $isSsm {
        background-image: linear-gradient(
          90deg,
          rgba(44, 54, 92, 0) 0%,
          rgba(44, 54, 92, 0.8) 15%,
          rgba(44, 54, 92, 0.8) 85%,
          rgba(44, 54, 92, 0) 100%
        );
      }
    }
  }
}
</style>
